import { useCallback, useEffect, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * A request id, behind a disclosure.
 *
 * The contract is explicit that a raw error message or a request id must never
 * be the primary text on an error screen - an operator needs "you do not have
 * permission", not a UUID. But support escalation needs that UUID, so it lives
 * here: collapsed by default, one click to copy.
 */
export function CopyableId({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}): JSX.Element {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Clipboard access can be denied by permissions policy. The id is visible
      // in the <code> element regardless, so this degrades to select-and-copy.
    }
  }, [value]);

  return (
    <details className={cn('group text-xs', className)}>
      <summary
        className={cn(
          'inline-flex cursor-pointer list-none items-center gap-1 text-muted-foreground underline underline-offset-2',
          'hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        )}
      >
        Details
      </summary>
      <div className="mt-1.5 flex items-center gap-1.5">
        <span className="text-muted-foreground">{label}</span>
        <code className="min-w-0 flex-1 truncate rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-secondary-foreground">
          {value}
        </code>
        <button
          type="button"
          onClick={copy}
          className={cn(
            'shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
          aria-label={copied ? 'Copied' : `Copy ${label.toLowerCase()}`}
        >
          {copied ? (
            <Check className="size-3 text-success" aria-hidden />
          ) : (
            <Copy className="size-3" aria-hidden />
          )}
        </button>
        {/* Announced without moving focus, so the copy is confirmed to a screen
            reader as well as to the eye. */}
        <span aria-live="polite" className="sr-only">
          {copied ? 'Copied to clipboard' : ''}
        </span>
      </div>
    </details>
  );
}
